import { z } from 'zod';

const Text = z.string().trim().min(1).max(12_000);
const Id = z.string().trim().min(1).max(200);

/** Hidden task data. It grants no tools or authority; the API supplies the task rules. */
export const CoachCopilotContextSchema = z.discriminatedUnion('task', [
  z
    .object({
      task: z.literal('create-video-script'),
      brief: Text.optional(),
      contentKind: Text.optional(),
      briefNodeId: Id.optional(),
      chosenIdea: Text.optional(),
      instructions: Text.optional(),
    })
    .strict(),
  z
    .object({
      task: z.literal('revise-video-script'),
      projectNodeId: Id,
      feedback: Text,
      brief: Text.optional(),
      briefNodeId: Id.optional(),
      instructions: Text.optional(),
    })
    .strict(),
]);
export type CoachCopilotContext = z.infer<typeof CoachCopilotContextSchema>;

const Envelope = z.object({
  coach: z.object({
    journeyId: z.enum(['content-studio', 'copilot-first-post', 'welcome-to-poppy']),
    boardId: Id,
    stepId: z.string(),
    context: CoachCopilotContextSchema.optional(),
  }),
});

/** Follow-up answers retain a task only while the matching guided stage is active. */
export function coachScriptTask(
  metadata: unknown,
  boardId: string
): CoachCopilotContext['task'] | null {
  const parsed = Envelope.safeParse(metadata);
  if (!parsed.success || parsed.data.coach.boardId !== boardId) return null;
  const { journeyId, stepId } = parsed.data.coach;
  const creating =
    journeyId !== 'copilot-first-post'
      ? ['prompt', 'writing', 'script-reply', 'script-error']
      : ['script-request', 'writing', 'script-help', 'script-error'];
  const revising =
    journeyId !== 'copilot-first-post'
      ? ['refine-request', 'refining', 'refine-reply', 'refine-error']
      : ['revision-request', 'revising', 'revision-check', 'revision-error'];
  return creating.includes(stepId)
    ? 'create-video-script'
    : revising.includes(stepId)
      ? 'revise-video-script'
      : null;
}

export function coachCopilotContext(
  metadata: unknown,
  boardId: string
): CoachCopilotContext | null {
  const parsed = Envelope.safeParse(metadata);
  if (!parsed.success) return null;
  const context = parsed.data.coach.context;
  return context && coachScriptTask(metadata, boardId) === context.task ? context : null;
}
